import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormHelperText,
  Autocomplete,
  Chip
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { fetchShiftById, createShift, updateShift } from '../../redux/slices/shiftsSlice';

import { fetchDrivers } from '../../redux/slices/driversSlice';

import { fetchVehicles } from '../../redux/slices/vehiclesSlice';

import PageHeader from '../common/PageHeader';

import Loading from '../common/Loading';

import ErrorMessage from '../common/ErrorMessage';

// Dayjs auf Deutsch einstellen
dayjs.locale('de');

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const ShiftForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const isEditMode = !!id;
  
  const { currentShift, isLoading: shiftLoading, error: shiftError } = useSelector(state => state.shifts);
  const { drivers, isLoading: driversLoading } = useSelector(state => state.drivers);
  const { vehicles, isLoading: vehiclesLoading } = useSelector(state => state.vehicles);
  
  // Pre-filled data from quick add
  const prefilledData = location.state || {};
  
  const [formData, setFormData] = useState({
    driver_id: prefilledData.driverId || '',
    vehicle_id: '',
    start_time: prefilledData.startTime ? dayjs(prefilledData.startTime) : dayjs(),
    end_time: prefilledData.endTime ? dayjs(prefilledData.endTime) : dayjs().add(8, 'hour'),
    notes: ''
  });
  
  const [formErrors, setFormErrors] = useState({
    driver_id: '',
    vehicle_id: '',
    start_time: '',
    end_time: ''
  });
  
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchShiftById(id));
    }
    
    dispatch(fetchDrivers());
    dispatch(fetchVehicles());
  }, [dispatch, id, isEditMode]);
  
  useEffect(() => {
    if (isEditMode && currentShift) {
      setFormData({
        ...formData,
        driver_id: currentShift.driver_id || '',
        vehicle_id: currentShift.vehicle_id || '',
        start_time: currentShift.start_time ? dayjs(currentShift.start_time) : dayjs(),
        end_time: currentShift.end_time ? dayjs(currentShift.end_time) : dayjs().add(8, 'hour'),
        notes: currentShift.notes || ''
      });
    }
  }, [currentShift, isEditMode]);
  
  useEffect(() => {
    // Filter available drivers
    if (drivers && drivers.length > 0) {
      const availableDrivers = isEditMode
        ? drivers.filter(driver => 
            driver.status !== 'busy' || 
            (currentShift && driver.id === currentShift.driver_id)
          )
        : drivers.filter(driver => driver.status !== 'busy');
      
      setAvailableDrivers(availableDrivers);
    }
    
    // Filter available vehicles
    if (vehicles && vehicles.length > 0) {
      const availableVehicles = isEditMode
        ? vehicles.filter(vehicle => 
            vehicle.status === 'active' && 
            (!vehicle.current_driver_id || 
              (currentShift && vehicle.id === currentShift.vehicle_id))
          )
        : vehicles.filter(vehicle => 
            vehicle.status === 'active' && !vehicle.current_driver_id
          );
      
      setAvailableVehicles(availableVehicles);
    }
  }, [drivers, vehicles, isEditMode, currentShift]);
  
  // When driver is selected, try to auto-select their vehicle if available
  useEffect(() => {
    if (formData.driver_id && drivers && vehicles) {
      const selectedDriver = drivers.find(d => d.id === formData.driver_id);
      
      if (selectedDriver && selectedDriver.vehicle_id) {
        const driverVehicle = vehicles.find(v => v.id === selectedDriver.vehicle_id);
        
        if (driverVehicle && availableVehicles.some(v => v.id === driverVehicle.id)) {
          setFormData({
            ...formData,
            vehicle_id: driverVehicle.id
          });
          
          // Clear vehicle error if present
          if (formErrors.vehicle_id) {
            setFormErrors({
              ...formErrors,
              vehicle_id: ''
            });
          }
        }
      }
    }
  }, [formData.driver_id, drivers, vehicles, availableVehicles]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Reset errors when field changes
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const handleDriverChange = (event, driver) => {
    setFormData({
      ...formData,
      driver_id: driver ? driver.id : ''
    });
    
    if (formErrors.driver_id) {
      setFormErrors({
        ...formErrors,
        driver_id: ''
      });
    }
  };
  
  const handleVehicleChange = (event, vehicle) => {
    setFormData({
      ...formData,
      vehicle_id: vehicle ? vehicle.id : ''
    });
    
    if (formErrors.vehicle_id) {
      setFormErrors({
        ...formErrors,
        vehicle_id: ''
      });
    }
  };
  
  const handleStartTimeChange = (date) => {
    setFormData({
      ...formData,
      start_time: date
    });
    
    if (formErrors.start_time) {
      setFormErrors({
        ...formErrors,
        start_time: ''
      });
    }
  };
  
  const handleEndTimeChange = (date) => {
    setFormData({
      ...formData,
      end_time: date
    });
    
    if (formErrors.end_time) {
      setFormErrors({
        ...formErrors,
        end_time: ''
      });
    }
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    // Validate driver
    if (!formData.driver_id) {
      newErrors.driver_id = 'Bitte wählen Sie einen Fahrer aus';
      valid = false;
    }
    
    // Validate vehicle
    if (!formData.vehicle_id) {
      newErrors.vehicle_id = 'Bitte wählen Sie ein Fahrzeug aus';
      valid = false;
    }
    
    // Validate start time
    if (!formData.start_time) {
      newErrors.start_time = 'Startzeit ist erforderlich';
      valid = false;
    }
    
    // Validate end time
    if (!formData.end_time) {
      newErrors.end_time = 'Endzeit ist erforderlich';
      valid = false;
    } else if (formData.end_time <= formData.start_time) {
      newErrors.end_time = 'Endzeit muss nach der Startzeit liegen';
      valid = false;
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const shiftData = {
        driver_id: formData.driver_id,
        vehicle_id: formData.vehicle_id,
        start_time: formData.start_time.toISOString(),
        end_time: formData.end_time.toISOString(),
        notes: formData.notes
      };
      
      if (isEditMode) {
        // Update existing shift
        dispatch(updateShift({
          id,
          ...shiftData
        })).then((result) => {
          if (!result.error) {
            navigate(`/schichten/${id}`);
          }
        });
      } else {
        // Create new shift
        dispatch(createShift(shiftData)).then((result) => {
          if (!result.error) {
            navigate('/schichten');
          }
        });
      }
    }
  };
  
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/schichten/${id}`);
    } else {
      navigate('/schichten');
    }
  };
  
  const isLoading = shiftLoading || driversLoading || vehiclesLoading;
  
  if (isLoading && isEditMode) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (shiftError && isEditMode) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={shiftError}
        onRetry={() => dispatch(fetchShiftById(id))}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Schicht bearbeiten' : 'Neue Schicht anlegen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichten', path: '/schichten' },
          { label: isEditMode ? 'Bearbeiten' : 'Neu', path: isEditMode ? `/schichten/${id}/bearbeiten` : '/schichten/neu' }
        ]}
      />
      
      <form onSubmit={handleSubmit}>
        {/* Hauptinformationen */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Schicht-Informationen
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="driver-select"
                options={availableDrivers}
                getOptionLabel={(option) => option.user?.name || 'Unbekannter Fahrer'}
                value={availableDrivers.find(d => d.id === formData.driver_id) || null}
                onChange={handleDriverChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Fahrer" 
                    variant="outlined"
                    required
                    error={!!formErrors.driver_id}
                    helperText={formErrors.driver_id}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1">{option.user?.name || 'Unbekannter Fahrer'}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.user?.phone || 'Keine Telefonnummer'} | Bewertung: {option.rating || 'N/A'}
                      </Typography>
                    </Box>
                    <Chip 
                      label={option.status === 'available' ? 'Verfügbar' : 
                             option.status === 'busy' ? 'Beschäftigt' : 'Offline'}
                      color={option.status === 'available' ? 'success' : 
                             option.status === 'busy' ? 'error' : 'default'}
                      size="small"
                    />
                  </Box>
                )}
              />
              <FormHelperText>
                {availableDrivers.length === 0 ? 
                  'Keine verfügbaren Fahrer gefunden.' : 
                  'Wählen Sie einen Fahrer für diese Schicht aus.'}
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="vehicle-select"
                options={availableVehicles}
                getOptionLabel={(option) => `${option.model} (${option.license_plate})`}
                value={availableVehicles.find(v => v.id === formData.vehicle_id) || null}
                onChange={handleVehicleChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Fahrzeug" 
                    variant="outlined"
                    required
                    error={!!formErrors.vehicle_id}
                    helperText={formErrors.vehicle_id}
                  />
                )}
              />
              <FormHelperText>
                {availableVehicles.length === 0 ? 
                  'Keine verfügbaren Fahrzeuge gefunden.' : 
                  'Wählen Sie ein Fahrzeug für diese Schicht aus.'}
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Startzeit"
                  value={formData.start_time}
                  onChange={handleStartTimeChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!formErrors.start_time,
                      helperText: formErrors.start_time
                    }
                  }}
                  ampm={false}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Endzeit"
                  value={formData.end_time}
                  onChange={handleEndTimeChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!formErrors.end_time,
                      helperText: formErrors.end_time
                    }
                  }}
                  ampm={false}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notizen"
                multiline
                rows={4}
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </FormPaper>
        
        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading}
          >
            {isEditMode ? 'Änderungen speichern' : 'Schicht erstellen'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ShiftForm;